import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, useColorModeValue, Center, Spinner } from '@chakra-ui/react';
import { useRecoilValue } from 'recoil';
import Layout from './components/layout/Layout';
import AppInitializer from './components/common/AppInitializer';
import ProtectedRoute from './components/common/ProtectedRoute';
import { userAtom, authLoadingAtom } from './state/userAtom';
import { MARKETING_SITE_ENABLED } from './config/features';
import LoginPage from './pages/LoginPage';
import SalesPage from './pages/SalesPage';
import DocumentationPage from './pages/DocumentationPage';
import InstallationPage from './pages/InstallationPage';
import ApiReferencePage from './pages/ApiReferencePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import DashboardPage from './pages/DashboardPage';
import AnalyticsPage from './pages/AnalyticsPage';
import InsightsPage from './pages/InsightsPage';
import CampaignsPage from './pages/CampaignsPage';
import AdsPage from './pages/AdsPage';
import ZonesPage from './pages/ZonesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import SitesPage from './pages/SitesPage';
import LinksPage from './pages/LinksPage';
import AdvertisersPage from './pages/AdvertisersPage';

// Redirect component for home route
function HomeRedirect() {
  const user = useRecoilValue(userAtom);
  const authLoading = useRecoilValue(authLoadingAtom);

  if (authLoading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Center>
    );
  }

  if (user) return <Navigate to="/dashboard" replace />;
  return MARKETING_SITE_ENABLED ? <SalesPage /> : <Navigate to="/login" replace />;
}

// Gate the public marketing/docs pages behind the feature flag.
function MarketingRoute({ children }) {
  return MARKETING_SITE_ENABLED ? children : <Navigate to="/" replace />;
}

// Reset scroll to top on route change (skip when navigating to a #section anchor).
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  return (
    <AppInitializer>
      <Box minH="100vh" bg={bgColor} transition="background-color 0.2s">
        <ScrollToTop />
        <Routes>
          {/* Redirect home to dashboard or login */}
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Public marketing / docs pages (gated by MARKETING_SITE_ENABLED) */}
          <Route path="/docs" element={<MarketingRoute><DocumentationPage /></MarketingRoute>} />
          <Route path="/docs/installation" element={<MarketingRoute><InstallationPage /></MarketingRoute>} />
          <Route path="/docs/api" element={<MarketingRoute><ApiReferencePage /></MarketingRoute>} />
          <Route path="/about" element={<MarketingRoute><AboutPage /></MarketingRoute>} />
          <Route path="/contact" element={<MarketingRoute><ContactPage /></MarketingRoute>} />
          <Route path="/privacy" element={<MarketingRoute><PrivacyPage /></MarketingRoute>} />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/analytics/:tab" element={<AnalyticsPage />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/advertisers" element={<AdvertisersPage />} />
                    <Route path="/ads" element={<AdsPage />} />
                    <Route path="/zones" element={<ZonesPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/reports/:tab" element={<ReportsPage />} />
                    <Route path="/insights" element={<InsightsPage />} />
                    <Route path="/sites" element={<SitesPage />} />
                    <Route path="/links" element={<LinksPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>
    </AppInitializer>
  );
}

export default App;
